export type MenuNavigationKey = 'ArrowDown' | 'ArrowUp' | 'Home' | 'End';

const MENU_NAVIGATION_KEYS = new Set<string>(['ArrowDown', 'ArrowUp', 'Home', 'End']);

/**
 * A null blur destination is not an outside interaction: Safari can blur the
 * focused item before clicking another button without focusing that button.
 * Removing the menu at that point loses its pending click. Outside pointer
 * dismissal and Escape remain the responsibility of the menu's listeners.
 */
export function isMenuFocusMovingOutside(container: Node, nextTarget: EventTarget | null): boolean {
    return nextTarget instanceof Node && !container.contains(nextTarget);
}

/**
 * Move focus within an already-filtered menu-item list.
 *
 * Keeping the wrap/home/end rules here prevents each dropdown from developing
 * subtly different keyboard behavior as new actions are registered.
 */
export function focusMenuItemForKey(
    items: readonly HTMLElement[],
    key: string,
    activeElement: Element | null = document.activeElement,
): key is MenuNavigationKey {
    if (!MENU_NAVIGATION_KEYS.has(key) || items.length === 0) return false;

    const currentIndex = items.indexOf(activeElement as HTMLElement);
    let nextIndex = 0;

    if (key === 'End') nextIndex = items.length - 1;
    else if (key === 'ArrowUp') nextIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
    else if (key === 'ArrowDown') {
        nextIndex = currentIndex < 0 || currentIndex === items.length - 1 ? 0 : currentIndex + 1;
    }

    items[nextIndex].focus();
    return true;
}
